import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { ThemeProvider } from "@mui/material/styles";
import Table_theme from "../Themes/TableTheme";
import FormThemes from "../Themes/FormThemes";
import { ReactComponent as DeleteIcon } from "../Icons/Delete.svg";
import { ReactComponent as SettingsIcon } from "../Icons/Settings.svg";
import CloseIcon from "@mui/icons-material/Close";
import MuiAlert from "@mui/material/Alert";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

import {
  TextField,
  CssBaseline,
  Grid,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from "@mui/material";
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  PaginationItem,
} from "@mui/material";
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  InputProps,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Snackbar,
  Popover,
  InputAdornment,
  Switch,
  FormControlLabel,
  Typography,
} from "@mui/material";

import {
  Add,
  Delete,
  Edit,
  Flag,
  FilterList,
  Settings,
  Search,
  Description,
} from "@mui/icons-material";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const displayColumns = ["dept_name", "description"];

const formFields = ["dept_name", "description"];

const fieldLabels = {};
displayColumns.forEach((field) => {
  if (field === "dept_name") {
    fieldLabels[field] = "Department Name";
  } else {
    fieldLabels[field] = field
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());
  }
});

const Department = () => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [depts, setDepts] = useState([]);
  const [filters, setFilters] = useState({});
  const [anchorEls, setAnchorEls] = useState({});
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const initial = {};
    displayColumns.forEach((f) => (initial[f] = true));
    return initial;
  });
  const [columnAnchorEl, setColumnAnchorEl] = useState(null);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [newDept, setNewDept] = useState({
    bo_id: "BFXIND",
    delete_flag: "N",
    data_source: "UI",
    created_by: "system",
    created_on_date: new Date().toISOString().split("T")[0],
  });
  const [EditDept, setEditDept] = useState({});
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [search, setSearch] = useState("");

  const rowsPerPage = 10;

  const fetchData = useCallback(async () => {
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        ...filters,
        search: search.trim(),
      };

      const response = await axios.get(
        `${BASE_URL}/departmentController/paginated`,
        { params }
      );
      setDepts(response.data.data);
      setTotalCount(response.data.total);
    } catch (err) {
      console.error("Error to get department details:", err);
    }
  }, [page, filters, search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const renderField = (field, state, setState, isEditMode = false) => {
    const handleChange = (e) => {
      const { name, value } = e.target;
      setState((prev) => ({
        ...prev,
        [name]: value,
      }));
    };
    return (
      <TextField
        key={field}
        label={fieldLabels[field] || field}
        name={field}
        value={state[field] || ""}
        onChange={handleChange}
        fullWidth
        size="small"
        margin="normal"
        error={Boolean(fieldErrors?.[field])}
        helperText={fieldErrors?.[field] || ""}
        sx={{
          fontSize: "12px",
          fontFamily: "Arial",
          "& .MuiInputBase-input": {
            fontSize: "12px",
            fontFamily: "Arial",
          },
          "& .MuiInputLabel-root": {
            fontSize: "12px",
            fontFamily: "Arial",
          },
        }}
      >
      </TextField>
    );
  };

  const checkDeptIdExists = async (dept_id) => {
    try {
      const res = await axios.get(
        `${BASE_URL}/departmentController/exists/${dept_id}`
      );
      return res.data.exists;
    } catch (err) {
      console.error("Error checking Department ID:", err);
      return false; // Safe fallback
    }
  };
  const handleAdd = async () => {
    const errors = {};
    const { dept_id } = newDept;
    // Check if Dept ID already exists
    const exists = await checkDeptIdExists(dept_id);
    if (exists) {
      errors.dept_id = "Department ID already exists";
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    try {
      await axios.post(`${BASE_URL}/departmentController`, newDept);
      setOpen(false);
      setNewDept({
        delete_flag: "N",
        data_source: "UI",
        created_by: "system",
        created_on_date: new Date().toISOString().split("T")[0],
      });
      setSnackbarMsg("Department added successfully");
      setShowSnackbar(true);
      fetchData();
    } catch (err) {
      console.error("Add failed:", err.response?.data || err.message);
      setSnackbarMsg("Failed to add Department");
      setShowSnackbar(true);
    }
  };

  const handleEdit = async () => {
    try {
      const { dept_id, bo_id, dept_name, description } = EditDept;

      await axios.put(`${BASE_URL}/departmentController`, {
        dept_id,
        bo_id,
        dept_name,
        description,
      });

      setEditOpen(false);
      setSnackbarMsg("Record updated");
      setShowSnackbar(true);
      fetchData();
    } catch (err) {
      console.error("Update failed:", err.response?.data || err.message, err);
      setSnackbarMsg("Failed to update Department");
      setShowSnackbar(true);
    }
  };

  const handleFilterIconClick = (field, event) => {
    setAnchorEls((prev) => ({ ...prev, [field]: event.currentTarget }));
  };

  const handleFilterClose = (field) => {
    setAnchorEls((prev) => ({ ...prev, [field]: null }));
  };

  const handleDelete = async (dept_id) => {
    const bo_id = "BFXIND";
    try {
      await axios.delete(
        `${BASE_URL}/departmentController/${dept_id}/${bo_id}`
      );
      setSnackbarMsg("Record has been deleted");
      setShowSnackbar(true);
      fetchData();
    } catch (err) {
      console.error("Delete failed:", err.response?.data || err.message);
      setSnackbarMsg("Failed to delete asset");
      setShowSnackbar(true);
    }
  };

  const handleToggleFlag = async (dept_id, currentFlag) => {
    const newFlag = currentFlag === "Y" ? "N" : "Y";

    try {
      const res = await fetch(
        `${BASE_URL}/departmentController/${dept_id}/toggle-flag`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ delete_flag: newFlag }),
        }
      );

      if (res.ok) {
        setDepts((prev) =>
          prev.map((dep) =>
            dep.dept_id === dept_id ? { ...dep, delete_flag: newFlag } : dep
          )
        );
      } else {
        console.error("Failed to update flag");
      }
    } catch (error) {
      console.error("Error updating flag:", error);
    }
  };
  return (
    <ThemeProvider theme={Table_theme}>
      <Container maxWidth='xl'>
        <Typography
          variant="h5"
          gutterBottom
          fontSize="20px"
          fontWeight="600"
        >
          Departments
        </Typography>
        <Box
          display="flex"
          justifyContent="flex-start"
          alignItems="center"
          // mt={0.5}
          mb={2}
          gap={1.5}
        >
          <IconButton onClick={(e) => setColumnAnchorEl(e.currentTarget)}>
            <SettingsIcon />
          </IconButton>
          <Popover
            open={Boolean(columnAnchorEl)}
            anchorEl={columnAnchorEl}
            onClose={() => setColumnAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{
              sx: {
                mt: 1,
                p: 1,
                width: 200,
                maxHeight: 300,
                overflowY: "auto",
                borderRadius: 2,
              },
            }}
          >
            {displayColumns.map((field) => (
              <MenuItem key={field} disableGutters sx={{ p: 0, mb: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                    border: "1px solid #ccc",
                    borderRadius: 1.5,
                    px: 2,
                    py: 1,
                    backgroundColor: "#fff",
                  }}
                >
                  <Typography
                    sx={{

                      color: "#333",
                      fontWeight: "bold",
                    }}
                  >
                    {fieldLabels[field]}
                  </Typography>
                  <Switch
                    size="small"
                    checked={columnVisibility[field]}
                    onChange={() =>
                      setColumnVisibility((prev) => ({
                        ...prev,
                        [field]: !prev[field],
                      }))
                    }
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#2DB0E4",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                      {
                        backgroundColor: "#2DB0E4",
                      },
                    }}
                  />
                </Box>
              </MenuItem>
            ))}
          </Popover>

          <Button
            variant="contained"
            size="small"
            onClick={() => setOpen(true)}
            sx={{
              fontWeight: "bold",
              borderRadius: "8px",
              px: 2.5,
              height: 36,
              backgroundColor: "#2DB0E4",
            }}
            startIcon={<Add />}
          >
            Add
          </Button>
          <TextField
            size="small"
            sx={{ width: "250px", height: 34 }}
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              sx: {
                height: 36, // Reduce overall input height
                paddingRight: 0,
              },
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box
          sx={{
            width: "100%",           // <-- ensures it fills the available main content space
            overflowX: "auto",       // <-- useful for tables to scroll on smaller screens
            p: { xs: 1, sm: 2 },      // optional padding
          }}
        >
          <ThemeProvider theme={Table_theme}>
            <CssBaseline />
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        whiteSpace: "nowrap",
                        textAlign: "center",
                        fontWeight: "bold",
                      }}
                    >
                      Actions
                    </TableCell>

                    {displayColumns
                      .filter((f) => columnVisibility[f])
                      .map((field) => (
                        <TableCell
                          key={field}
                          sx={{
                            whiteSpace: "nowrap",
                            textAlign: "left",
                            verticalAlign: "middle",
                          }}
                        >
                          <Box
                            display="inline-flex"
                            alignItems="center"
                            justifyContent="center"
                            sx={{ gap: 0.5 }}
                          >
                            <strong>{fieldLabels[field]}</strong>
                            <IconButton
                              size="small"
                              onClick={(e) => handleFilterIconClick(field, e)}
                            >
                              <FilterList fontSize="small" />
                            </IconButton>
                          </Box>

                          <Popover
                            open={Boolean(anchorEls[field])}
                            anchorEl={anchorEls[field]}
                            onClose={() => handleFilterClose(field)}
                            anchorOrigin={{
                              vertical: "bottom",
                              horizontal: "left",
                            }}
                          >
                            <Box
                              p={2}
                              display="flex"
                              flexDirection="column"
                              gap={1}
                              width={200}
                            >
                              <TextField
                                size="small"
                                fullWidth
                                placeholder={`Filter ${fieldLabels[field]}`}
                                value={filters[field] || ""}
                                onChange={(e) =>
                                  setFilters((prev) => ({
                                    ...prev,
                                    [field]: e.target.value,
                                  }))
                                }
                              />
                              <Box
                                display="flex"
                                justifyContent="space-between"
                              >
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={() => handleFilterClose(field)}
                                  sx={{ minWidth: 70 }}
                                >
                                  Apply
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => {
                                    setFilters((prev) => ({
                                      ...prev,
                                      [field]: "",
                                    }));
                                    handleFilterClose(field);
                                  }}
                                  sx={{ minWidth: 70 }}
                                >
                                  Clear
                                </Button>
                              </Box>
                            </Box>
                          </Popover>
                        </TableCell>
                      ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {depts.map((row, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Box display="flex" gap={0.5} justifyContent="center">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditDept(row);
                              setIsEditMode(true);
                              setEditOpen(true);
                            }}
                            sx={{ color: "#2DB0E4" }}
                          >
                            <EditOutlinedIcon sx={{ fontSize: "16px" }} />
                          </IconButton>

                          <IconButton
                            size="small"
                            onClick={() => handleDelete(row.dept_id)}
                          >
                            <DeleteIcon
                              sx={{ color: "#FF0C0C", width: 17, height: 17 }}
                            />
                          </IconButton>

                          <IconButton
                            size="small"
                            onClick={() =>
                              handleToggleFlag(row.dept_id, row.delete_flag)
                            }
                          >
                            <Flag
                              sx={{
                                color:
                                  row.delete_flag === "N"
                                    ? "#2E9F50"
                                    : "#FF0C0C",
                                width: 17,
                                height: 17,
                                transition: "color 0.2s",
                                verticalAlign: "middle",
                              }}
                            />
                          </IconButton>
                        </Box>
                      </TableCell>

                      {displayColumns
                        .filter((f) => columnVisibility[f])
                        .map((field) => (
                          <TableCell
                            key={field}
                            align="left"
                            sx={{

                              whiteSpace: "nowrap",
                              verticalAlign: "middle",
                            }}
                          >
                            {row[field]}
                          </TableCell>
                        ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box display="flex" justifyContent="center" mt={2}>
              <Pagination
                count={Math.ceil(totalCount / rowsPerPage)}
                page={page + 1}
                onChange={(_, newPage) => setPage(newPage - 1)}
                color="primary"
                variant="outlined"
                shape="rounded"
                sx={{
                  "& .MuiPaginationItem-root": {
                    borderRadius: "8px",
                    minWidth: "32px",
                    height: "32px",
                    fontSize: "14px",
                  },
                  "& .Mui-selected": {
                    backgroundColor: "#b3dbea !important",
                    color: "black",
                    fontWeight: "bold",
                  },
                  "& .MuiPaginationItem-ellipsis": {
                    display: "none",
                  },
                }}
              />
            </Box>
          </ThemeProvider>
        </Box>
        <ThemeProvider theme={FormThemes}>
          <Dialog
            open={open || editOpen}
            onClose={() => {
              setOpen(false);
              setEditOpen(false);
            }}
            fullWidth
            maxWidth="sm"
            sx={{
              "& .MuiDialog-paper": {
                borderRadius: 5,
                overflow: "visible",
                width: "500px", // Manually control the width if needed
                maxWidth: "95vw", // Responsive max
              },
            }}
          >
            <DialogTitle
              sx={{
                borderRadius: "12px",
                bgcolor: "#f5f5f5",
                p: 1,
                fontSize: "16px",
                fontWeight: 600,
              }}
            >
              {isEditMode ? "Update Departent" : "Add Departent"}
              <IconButton
                aria-label="close"
                onClick={() => {
                  setOpen(false);
                  setEditOpen(false);
                  setIsEditMode(false);
                  setFieldErrors({});
                }}
                sx={{ position: "absolute", right: 8, top: 5 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              {formFields.map((field, index) => (
                <Grid item xs={12} key={index}>
                  {renderField(
                    field,
                    isEditMode ? EditDept : newDept,
                    isEditMode ? setEditDept : setNewDept,
                    isEditMode
                  )}
                </Grid>
              ))}
            </DialogContent>

            <DialogActions sx={{ p: "8px 24px", justifyContent: "flex-end" }}>
              <Button
                onClick={() => {
                  setNewDept({}); //  Reset the form data
                  setOpen(false); // Then open the dialog
                  setEditOpen(false);
                  setIsEditMode(false);
                  setFieldErrors({});
                }}
                variant="outlined"
                color="inherit"
                sx={{ mr: 1, color: "#555", borderColor: "#ccc" }}
              >
                Cancel
              </Button>
              <Button
                onClick={isEditMode ? handleEdit : handleAdd}
                variant="contained"
                color="primary"
                sx={{
                  fontWeight: "bold",
                  borderRadius: "8px",
                  px: 2.5,
                  height: 36,
                  ...(isEditMode
                    ? {
                      backgroundColor: "#4CAF50",
                      "&:hover": { backgroundColor: "#45a049" },
                    }
                    : {
                      backgroundColor: "#2DB0E4",
                      "&:hover": { backgroundColor: "#1D87B0" },
                    }),
                }}
              >
                {isEditMode ? "Update" : "Save"}
              </Button>
            </DialogActions>
          </Dialog>
        </ThemeProvider>
        <Snackbar
          open={showSnackbar}
          autoHideDuration={3000}
          onClose={() => setShowSnackbar(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setShowSnackbar(false)}
            severity="success"
            sx={{ width: "100%" }}
          >
            {snackbarMsg}
          </Alert>
        </Snackbar>
      </Container>
    </ThemeProvider>
  );
};

export default Department;
